import { useMemo } from "react";
import {
  DataTable,
  DateColumnFilter,
  // DateColumnFilter, SelectColumnFilter
} from "../DataTable";
import OverlayTooltip from "../OverlayTooltip";

import moment from "moment";

import {
  useGetPotentialOrdersForReturn,
  useGetReturnPermission,
  useIsFetchingDealerInfo,
} from "./manualReturns.utils";
import { isProd } from "../../helpers/utilities";

const formatData = (data, allowMakeReturns = true, dealerInfo) => {
  if (!data || !Array.isArray(data)) return [];

  const exlucdeDealerId = isProd() ? [22622] : [];

  const excludeData = data.filter(
    (item) =>
      item?.stat.toLowerCase() !== "v" ||
      !exlucdeDealerId.includes(item?.clientid)
  ); // Exclude voided orders

  return excludeData.map((item) => ({
    tranid: item?.tranid,
    invoice: item?.invoice || "",
    deliveryDate: item?.tdate || "",
    creationDate: item?.entered || "",
    dealerName: dealerInfo?.dealerName || "",
    address: item?.address || "",
    state: item?.st || "",
    city: item?.city || "",
    zip: item?.zip || "",
    comment: item?.comment || "",
    piece: item?.piece || "",
    allowMakeReturns: allowMakeReturns,
  }));
};

function DealerCell({ row }) {
  const { original } = row;

  return (
    <div className="tw-flex tw-items-start tw-flex-col">
      <div className="tw-whitespace-normal">{original.dealerName}</div>
      <div className="tw-whitespace-normal">{`${original.address} ${original.city} ${original.state} ${original.zip}`}</div>
    </div>
  );
}

function ActionCell({ row }) {
  const { original } = row;

  if (original?.allowMakeReturns === false) {
    return (
      <div className="tw-w-full tw-text-center">
        <OverlayTooltip
          label={
            <>
              Unable to start a return.
              <br /> Please contact Dealer to initiate a return for this order.
            </>
          }
        >
          <button className="btn btn-primary tw-text-base" disabled>
            Return
          </button>
        </OverlayTooltip>
      </div>
    );
  }

  return (
    <div className="tw-text-center tw-w-full">
      <button
        className="btn btn-primary tw-text-base"
        onClick={() => {
          window.open(
            `/ClientPortal/ActionCenter/ManualReturn/${original?.tranid}`,
            "_blank",
            "noreferrer noopener"
          );
        }}
      >
        Return
      </button>
    </div>
  );
}

function ManualReturnsTable({ searchManualReturns }) {
  const {
    data: response,
    isInitialLoading,
    isError,
  } = useGetPotentialOrdersForReturn(searchManualReturns);

  const {
    data: responseReturnPermission,
    isInitialLoading: isInitialLoadingPermissions,
    isError: isErrorPermissions,
  } = useGetReturnPermission(searchManualReturns?.dealerId);

  const isFetchingDealerInfo = useIsFetchingDealerInfo();

  const data = useMemo(() => {
    const permission = responseReturnPermission?.data;

    const isAllowMakeReturns = () => {
      if (!permission) return true;

      const isGlobalPermissionActive =
        permission?.clientReturnPermissions?.isDisabled ||
        permission?.clientReturnPermissions?.isByApproval;

      const isGlobalPersmissionDenied =
        permission?.clientReturnPermissions?.isDisabled &&
        !permission?.clientReturnPermissions?.isByApproval;

      const isIndividualPermissionDenied =
        permission?.customerReturnPermissions?.isActive &&
        !permission?.customerReturnPermissions?.byApproval;

      // Global Permission Denied
      if (isGlobalPermissionActive && isGlobalPersmissionDenied) {
        return false;
      }

      // Individual Permission Denied
      if (!isGlobalPermissionActive && isIndividualPermissionDenied) {
        return false;
      }

      return true;
    };

    return formatData(
      response?.data,
      isAllowMakeReturns(),
      searchManualReturns
    );
  }, [response?.data, responseReturnPermission?.data, searchManualReturns]);

  const columns = useMemo(
    () =>
      /** @type {Column[]} */ ([
        {
          Header: "Delivery ID",
          accessor: "tranid",
          minWidth: 300,
          width: 300,
          disableResizing: true,
        },
        {
          Header: "Invoice Number",
          accessor: "invoice",
        },

        {
          Header: "Delivery Date",
          accessor: "deliveryDate",
          Cell: ({ value }) => moment(value).format("MM/DD/YYYY"),
          Filter: DateColumnFilter,
          filter: "dateEqual",
        },
        {
          Header: "Creation Date",
          accessor: "creationDate",
          Cell: ({ value }) => moment(value).format("MM/DD/YYYY"),
          Filter: DateColumnFilter,
          filter: "dateEqual",
        },
        {
          Header: "Dealer",
          id: "address",
          accessor: (originalRow) =>
            `${originalRow.address} ${originalRow.city} ${originalRow.state} ${originalRow.zip}`,
          // Cell: withDealerInfo(searchManualReturns),
          Cell: DealerCell,
        },
        {
          Header: "Pieces",
          accessor: "piece",
        },
        {
          Header: () => <div className="tw-text-center">Options</div>,
          accessor: "originalData",
          disableFilters: true,
          minWidth: 50,
          width: 80,
          Cell: ActionCell,
        },
      ]),
    []
  );

  return (
    <DataTable
      isError={isError || isErrorPermissions}
      isLoading={
        isFetchingDealerInfo > 0 ||
        isInitialLoading ||
        isInitialLoadingPermissions
      }
      columns={columns}
      data={data}
      initialState={{
        hiddenColumns: ["id"],
      }}
      slotProps={{
        topToolbar: {
          disabled: true,
        },
      }}
    />
  );
}

export default ManualReturnsTable;

/**
 * @typedef {import('react-table').Column} Column
 */
